import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import { getRoleDisplay } from '../utils/roleUtils';
import { UserPlus, Search, Users, Trash2, Clock, Mail, X } from 'lucide-react';
import { invalidateTeamMembersCache } from '../utils/teamMembersCache';
import { UserAvatar } from '../components/common/UserAvatar';

interface TeamMember {
  id: string;
  username: string;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  is_searchable: boolean;  // Controls visibility in booking/swim lanes/searches
  created_at: string;
  photo_url?: string;
}

interface PendingInvite {
  id: string;
  email: string;
  status: string;
  created_at: string;
  expires_at: string;
}

const ROLE_OPTIONS = [
  { value: 'VET_STAFF', label: 'Vet Staff' },
  { value: 'PRACTICE_ADMIN', label: 'Practice Admin' },
];

const PracticeTeam: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;
  const isPracticeAdmin = user?.role === 'PRACTICE_ADMIN' || user?.role === 'SYSTEM_ADMIN';
  
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('VET_STAFF');
  const [isInviting, setIsInviting] = useState(false);
  
  // Edit modal state
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editForm, setEditForm] = useState({ full_name: '', role: '', is_searchable: true });
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  
  // Confirmation states
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null);
  const [removeMemberConfirm, setRemoveMemberConfirm] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  
  // Fetch team members and pending invites
  useEffect(() => {
    const fetchTeamData = async () => {
      if (!practiceId) {
        setError('No practice ID found');
        setIsLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('access_token');
        
        // Fetch team members
        const membersResponse = await fetch(
          `${API_BASE_URL}/api/v1/practices/${practiceId}/members`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (membersResponse.ok) {
          const membersData = await membersResponse.json();
          setMembers(membersData);
        }
        
        // Fetch pending invites
        const invitesResponse = await fetch(
          `${API_BASE_URL}/api/v1/practices/${practiceId}/invites`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
        
        if (invitesResponse.ok) {
          const invitesData = await invitesResponse.json();
          const pendingOnly = invitesData.filter((inv: PendingInvite) => inv.status === 'pending');
          setPendingInvites(pendingOnly);
        }
        
        setIsLoading(false);
      } catch (error: any) {
        console.error('Error fetching team data:', error);
        setError(error.message || 'Failed to load team data');
        setIsLoading(false);
      }
    };
    
    fetchTeamData();
  }, [practiceId]);
  
  // Handle sending an invitation
  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !practiceId) return;
    
    const emailLower = inviteEmail.trim().toLowerCase();
    const isAlreadyInvited = pendingInvites.some(
      i => i.email.toLowerCase() === emailLower && i.status === 'pending'
    );
    
    if (isAlreadyInvited) {
      setError('This email has already been invited');
      return;
    }
    
    setIsInviting(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(
        `${API_BASE_URL}/api/v1/practices/${practiceId}/invites`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email: inviteEmail.trim(),
            role: inviteRole
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send invitation');
      }
      
      const data = await response.json();
      setSuccessMessage(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('VET_STAFF');
      setPendingInvites([...pendingInvites, data]);
      
      // Invalidate team members cache so other pages refresh
      invalidateTeamMembersCache(practiceId);
      
      setTimeout(() => {
        setShowInviteModal(false);
        setSuccessMessage(null);
      }, 2000);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      setError(error.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };
  
  // Open edit modal for a member
  const openEditModal = (member: TeamMember) => {
    setEditingMember(member);
    setEditForm({
      full_name: member.full_name || '',
      role: member.role,
      is_searchable: member.is_searchable ?? true,
    });
  };

  // Handle saving member edits
  const handleSaveEdit = async () => {
    if (!practiceId || !editingMember) return;
    
    setIsSavingEdit(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token');
      const memberId = editingMember.id;
      const isOwnProfile = memberId === user?.id;
      
      // Update name if changed
      if (editForm.full_name.trim() !== (editingMember.full_name || '')) {
        const nameResponse = await fetch(
          `${API_BASE_URL}/api/v1/practices/${practiceId}/members/${memberId}`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ full_name: editForm.full_name.trim() })
          }
        );
        
        if (!nameResponse.ok) {
          const errorData = await nameResponse.json();
          throw new Error(errorData.detail || 'Failed to update name');
        }
      }
      
      // Update role if changed and not own profile
      if (!isOwnProfile && editForm.role !== editingMember.role) {
        const roleResponse = await fetch(
          `${API_BASE_URL}/api/v1/practices/${practiceId}/members/${memberId}/role`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ role: editForm.role })
          }
        );
        
        if (!roleResponse.ok) {
          const errorData = await roleResponse.json();
          throw new Error(errorData.detail || 'Failed to update role');
        }
      }
      
      // Update visibility if changed
      if (editForm.is_searchable !== (editingMember.is_searchable ?? true)) {
        const visibilityResponse = await fetch(
          `${API_BASE_URL}/api/v1/practices/${practiceId}/members/${memberId}/visibility`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ is_searchable: editForm.is_searchable })
          }
        );
        
        if (!visibilityResponse.ok) {
          const errorData = await visibilityResponse.json();
          throw new Error(errorData.detail || 'Failed to update visibility');
        }
        
        // Invalidate cache so swim lanes/booking views update
        invalidateTeamMembersCache(practiceId);
      }
      
      // Update local state
      setMembers(members.map(m => 
        m.id === memberId 
          ? { ...m, full_name: editForm.full_name.trim(), role: editForm.role, is_searchable: editForm.is_searchable } 
          : m
      ));
      setEditingMember(null);
      setSuccessMessage('Member updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: unknown) {
      console.error('Error updating member:', error);
      setError(error instanceof Error ? error.message : 'Failed to update member');
    } finally {
      setIsSavingEdit(false);
    }
  };
  
  // Handle revoking an invitation
  const handleRevokeInvite = async (inviteId: string) => {
    if (!practiceId) return;
    
    setIsRevoking(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(
        `${API_BASE_URL}/api/v1/practices/${practiceId}/invites/${inviteId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to revoke invitation');
      }
      
      setPendingInvites(pendingInvites.filter(inv => inv.id !== inviteId));
      setRevokeConfirm(null);
      setSuccessMessage('Invitation revoked');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error revoking invitation:', error);
      setError(error.message || 'Failed to revoke invitation');
    } finally {
      setIsRevoking(false);
    }
  };
  
  // Handle removing a team member
  const handleRemoveMember = async (memberId: string) => {
    if (!practiceId) return;
    
    setIsRemovingMember(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(
        `${API_BASE_URL}/api/v1/practices/${practiceId}/members/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove team member');
      }
      
      setMembers(members.filter(m => m.id !== memberId));
      setRemoveMemberConfirm(null);
      setSuccessMessage('Team member removed');
      
      // Invalidate team members cache so other pages refresh
      invalidateTeamMembersCache(practiceId);
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error removing team member:', error);
      setError(error.message || 'Failed to remove team member');
    } finally {
      setIsRemovingMember(false);
    }
  };
  
  // Filter members by search term
  const filteredMembers = members.filter(member => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      member.full_name?.toLowerCase().includes(term) ||
      member.email?.toLowerCase().includes(term) ||
      member.username?.toLowerCase().includes(term)
    );
  });
  
  const filteredInvites = pendingInvites.filter(invite => {
    if (!searchTerm.trim()) return true;
    return invite.email.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
            <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">Team Members</h1>
        </div>
        <p className="text-gray-600 dark:text-dark-text-secondary ml-16">
          Manage your practice team and send invitations
        </p>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-green-800 dark:text-green-300">{successMessage}</p>
        </div>
      )}
      
      {/* Search and Actions */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search team members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Invite Button */}
          {isPracticeAdmin && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <UserPlus className="w-4 h-4" />
              Invite Team Member
            </button>
          )}
        </div>
      </div>
      
      {/* Team Members Table */}
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-bg-tertiary border-b border-gray-200 dark:border-dark-border">
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Team Member
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Visibility
                </th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {filteredMembers.length === 0 && filteredInvites.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-dark-text-muted">
                    <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">No team members</p>
                    <p className="text-sm text-gray-500 dark:text-dark-text-muted">Invite members to join your practice.</p>
                  </td>
                </tr>
              ) : (
                <>
                  {/* Active Team Members */}
                  {filteredMembers.map((member) => (
                    <tr
                      key={member.id}
                      onClick={() => openEditModal(member)}
                      className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            photoUrl={member.photo_url}
                            fullName={member.full_name || member.email}
                            size="md"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                              {member.full_name || member.username}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-dark-text-muted">
                              @{member.username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-dark-text-secondary">
                        {member.email}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700 dark:text-dark-text-secondary">
                          {getRoleDisplay(member.role)}
                          {member.id === user?.id && (
                            <span className="ml-2 text-xs text-gray-400">(You)</span>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {member.is_searchable !== false ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300">
                            Visible
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            Hidden
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
                          Active
                        </span>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Pending Invites */}
                  {filteredInvites.map((invite) => (
                    <tr
                      key={invite.id}
                      className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors bg-gray-50/50 dark:bg-dark-bg-tertiary/50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-500 dark:text-dark-text-muted italic">
                              Pending Invite
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-dark-text-secondary">
                        {invite.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-dark-text-muted">
                        —
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-dark-text-muted">
                        —
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                          {isPracticeAdmin && (
                            <button
                              onClick={() => setRevokeConfirm(invite.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1"
                              title="Revoke invitation"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Edit Member Modal */}
      {editingMember && (
        <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                Edit Team Member
              </h3>
              <button
                onClick={() => setEditingMember(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-dark-border">
              <UserAvatar
                photoUrl={editingMember.photo_url}
                fullName={editingMember.full_name || editingMember.email}
                size="lg"
              />
              <div>
                <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                  @{editingMember.username}
                </div>
                <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                  {editingMember.email}
                </div>
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="fullName"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md shadow-sm bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter full name"
                disabled={isSavingEdit}
              />
            </div>
            
            {isPracticeAdmin && editingMember.id !== user?.id && (
              <div className="mb-6">
                <label htmlFor="editRole" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Role
                </label>
                <select
                  id="editRole"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md shadow-sm bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSavingEdit}
                >
                  {ROLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Visibility Toggle - visible for own profile or admin editing others */}
            {(editingMember.id === user?.id || isPracticeAdmin) && (
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
                      Visible in Booking & Calendar
                    </label>
                    <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-0.5">
                      When off, you won't appear in online booking, swim lanes, or searches
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditForm({ ...editForm, is_searchable: !editForm.is_searchable })}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      editForm.is_searchable ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                    disabled={isSavingEdit}
                    role="switch"
                    aria-checked={editForm.is_searchable}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        editForm.is_searchable ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}
            
            {isPracticeAdmin && editingMember.id !== user?.id && (
              <div className="mb-6 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  onClick={() => {
                    setRemoveMemberConfirm(editingMember.id);
                    setEditingMember(null);
                  }}
                  className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove from team
                </button>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingMember(null)}
                className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSavingEdit}
              >
                {isSavingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-4">
              Invite Team Member
            </h3>
            
            <form onSubmit={handleSendInvite}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md shadow-sm bg-white dark:bg-dark-bg-primary text-gray-900 dark:text-dark-text-primary focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="colleague@example.com"
                  required
                  disabled={isInviting}
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
                  Role
                </label>
                <div className="space-y-2">
                  {ROLE_OPTIONS.map(opt => (
                    <label
                      key={opt.value}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                        inviteRole === opt.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                          : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-dark-bg-primary'
                      } ${isInviting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="inviteRole"
                        value={opt.value}
                        checked={inviteRole === opt.value}
                        onChange={(e) => setInviteRole(e.target.value)}
                        disabled={isInviting}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                        inviteRole === opt.value
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 dark:border-gray-500'
                      }`}>
                        {inviteRole === opt.value && (
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                          {opt.label}
                        </span>
                        <p className="text-xs text-gray-500 dark:text-dark-text-tertiary mt-0.5">
                          {opt.value === 'VET_STAFF' 
                            ? 'Can view and edit within practice'
                            : 'Can manage team, invite, and remove members'
                          }
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteRole('VET_STAFF');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                  disabled={isInviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isInviting}
                >
                  {isInviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Revoke Confirmation Modal */}
      {revokeConfirm && (
        <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                Revoke Invitation
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-6">
              Are you sure you want to revoke this invitation? The recipient will no longer be able to accept it.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRevokeConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                disabled={isRevoking}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRevokeInvite(revokeConfirm)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRevoking}
              >
                {isRevoking ? 'Revoking...' : 'Revoke Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Remove Member Confirmation Modal */}
      {removeMemberConfirm && (
        <div className="fixed inset-0 bg-gray-500/75 dark:bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900 dark:text-dark-text-primary">
                Remove Team Member
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-6">
              Are you sure you want to remove this team member? They will lose access to all practice data.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRemoveMemberConfirm(null)}
                className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
                disabled={isRemovingMember}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleRemoveMember(removeMemberConfirm)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRemovingMember}
              >
                {isRemovingMember ? 'Removing...' : 'Remove Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeTeam;
